import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useRef, useState } from "react";
import { getAssetHistoryData } from "../../api/assetHistoryApi/assetHistoryApi";
import { DownstreamreprocessModel } from "../../types/DownstreamreprocessModel";
import moment from "moment";
import CloseIcon from "@mui/icons-material/Close";
import ExcelIcon from "../../assets/images/ExcelExport.png";
import MaximizeIcon from "@mui/icons-material/Fullscreen";
import RestoreIcon from "@mui/icons-material/FullscreenExit";
import LocalStorageUtil from "../../utils/localStorage";

interface AssetHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  selectedData: any;
}

const AssetHistory: React.FC<AssetHistoryProps> = ({
  isOpen,
  onClose,
  title,
  selectedData,
}) => {
  const [_loading, set_Loading] = useState<boolean>(false);// NOSONAR
  const gridRef = useRef<AgGridReact>(null);
  const [rowData, setRowData] = useState<any[]>([]);
  const [isMaximized, setIsMaximized] = useState<boolean>(false);
  const [columnDefs] = useState<ColDef[]>([
    {
      headerName: "#",
      width: 50,
      valueGetter: (params) => {
        return params.node ? (params.node.rowIndex ?? 0) + 1 : 0;
      },
      
    },
    {
      headerName: "Modified Date",
      field: "modifiedDate",
      enableRowGroup: true,
      width: 160,
      autoHeight: true,
      cellClass: "dateFormat",
      valueFormatter: (params) => {
        return params.value
          ? moment.utc(params.value).local().format("MM-DD-YYYY HH:mm:ss")
          : "";
      },
    },
    {
      headerName: "Status",
      field: "status",
      tooltipField: "status",
      enableRowGroup: true,
      width: 130,
      autoHeight: true,
      filter: "agTextColumnFilter",
      filterParams: {
        clearButton: true,
        applyButton: true,
        debounceMs: 200,
      },
    },
    {
      headerName: "Document ID",
      field: "documentID",
      tooltipField: "documentID",
      enableRowGroup: true,
      autoHeight: true,
      filter: "agTextColumnFilter",
      filterParams: {
        clearButton: true,
        applyButton: true,
        debounceMs: 200,
      },
    },
    {
      headerName: "File Name",
      field: "fileName",
      tooltipField: "fileName",
      enableRowGroup: true,
      width: 170,
      autoHeight: true,
      filter: "agTextColumnFilter",
      filterParams: {
        clearButton: true,
        applyButton: true,
        debounceMs: 200,
      },
    },
    {
      headerName: "File Size",
      field: "fileSize",
      tooltipField: "fileSize",
      enableRowGroup: true,
      width: 135,
      autoHeight: true,
      filter: "agTextColumnFilter",
      filterParams: {
        clearButton: true,
        applyButton: true,
        debounceMs: 200,
      },
    },
    {
      headerName: "Destination",
      field: "destination",
      tooltipField: "destination",
      enableRowGroup: true,
      width: 150,
      autoHeight: true,
      filter: "agTextColumnFilter",
      filterParams: {
        clearButton: true,
        applyButton: true,
        debounceMs: 200,
      },
    },
    {
      headerName: "MetaData Line Item",
      field: "metaDataLineItem",
      enableRowGroup: true,
      tooltipField: "metaDataLineItem",
      autoHeight: true,
      filter: "agTextColumnFilter",
      filterParams: {
        clearButton: true,
        applyButton: true,
        debounceMs: 200,
      },
      cellRenderer: (params: any) => {
        if (!params.value) {
          return "";
        }
        return params.value.length > 50
          ? params.value.substring(0, 50)
          : params.value;
      },
    },
    {
      headerName: "MetaData Header",
      field: "metaDataHeader",
      enableRowGroup: true,
      tooltipField: "metaDataHeader",
      autoHeight: true,
      filter: "agTextColumnFilter",
      filterParams: {
        clearButton: true,
        applyButton: true,
        debounceMs: 200,
      },
      cellRenderer: (params: any) => {
        if (!params.value) {
          return "";
        }
        return params.value.length > 50
          ? params.value.substring(0, 50)
          : params.value;
      },
    },
    {
      headerName: "MetaData File Name",
      field: "metaDataFileName",
      enableRowGroup: true,
      tooltipField: "metaDataFileName",
      autoHeight: true,
      filter: "agTextColumnFilter",
      width: 220,
      filterParams: {
        clearButton: true,
        applyButton: true,
        debounceMs: 200,
      },
      cellRenderer: (params: any) => {
        if (!params.value) {
          return "";
        }
        return params.value.length > 50
          ? params.value.substring(0, 50)
          : params.value;
      },
    },
    {
      headerName: "Rendition",
      field: "rendition",
      tooltipField: "rendition",
      enableRowGroup: true,
      width: 135,
      autoHeight: true,
      filter: "agTextColumnFilter",
      filterParams: {
        clearButton: true,
        applyButton: true,
        debounceMs: 200,
      },
    },
    {
      headerName: "Source",
      field: "source",
      tooltipField: "source",
      enableRowGroup: true,
      width: 135,
      autoHeight: true,
      filter: "agTextColumnFilter",
      filterParams: {
        clearButton: true,
        applyButton: true,
        debounceMs: 200,
      },
    },
    {
      headerName: "Source Version",
      field: "sourceVersion",
      tooltipField: "sourceVersion",
      enableRowGroup: true,
      width: 170,
      autoHeight: true,
      filter: "agTextColumnFilter",
      filterParams: {
        clearButton: true,
        applyButton: true,
        debounceMs: 200,
      },
    },
    {
      headerName: "MetaData Location",
      field: "metaDataLocation",
      enableRowGroup: true,
      tooltipField: "metaDataLocation",
      autoHeight: true,
      filter: "agTextColumnFilter",
      filterParams: {
        clearButton: true,
        applyButton: true,
        debounceMs: 200,
      },
      cellRenderer: (params: any) => {
        if (!params.value) {
          return "";
        }
        return params.value.length > 50
          ? params.value.substring(0, 50)
          : params.value;
      },
    },
    {
      headerName: "Asset Location",
      field: "assetLocation",
      enableRowGroup: true,
      tooltipField: "assetLocation",
      autoHeight: true,
      filter: "agTextColumnFilter",
      filterParams: {
        clearButton: true,
        applyButton: true,
        debounceMs: 200,
      },
    },
    {
      headerName: "Modified By",
      field: "modifiedBy",
      tooltipField: "modifiedBy",
      width: 150,
      autoHeight: true,
      filter: "agTextColumnFilter",
      filterParams: {
        clearButton: true,
        applyButton: true,
        debounceMs: 200,
      },
    },
    {
      headerName: "DocDistro Worker Name",
      field: "docDistroWorkerName",
      tooltipField: "DocDistroWorkerName",
      enableRowGroup: true,
      width: 440,
      autoHeight: true,
      filter: "agTextColumnFilter",
      filterParams: {
        clearButton: true,
        applyButton: true,
        debounceMs: 200,
      },
      cellRenderer: (params: any) => {
        if (!params.value) {
          return "";
        }
        return params.value.length > 50
          ? params.value.substring(0, 50)
          : params.value;
      },
    },
   
  ]);

  useEffect(() => {
    const params: DownstreamreprocessModel = {
      ProcessType: 0,
      DocumentStreamId: selectedData.DocumentStreamID,
      AccountId: 0,
      UserId: 0,
      FileName: "",
      Serverpath: "",
      DocumentLink: "",
      Recipients: "",
      CCEmail: "",
      Subject: "",
      SubSystemConfigurationID: "",
      ParentDocumentStreamID: 0,
      SubSystemName: "",
      DocumentStatus: 0,
      TimeZone: "",
      BusinessUnitId: selectedData.BusinessUnitID,
      ContextName: LocalStorageUtil.getItem("contextName")?.toString(),
    };
    fetchAssetHistoryData(params);
  }, []);

  const fetchAssetHistoryData = async (params: any) => {
    try {
      set_Loading(true);
      const response = await getAssetHistoryData(params);
      if (response.getAssetHistoryDetails.length > 0) {
        setRowData(response.getAssetHistoryDetails);
      } else {
        setRowData([]);
      }
    } catch (error) {
      console.error("Error in Asset History data fetching ", error);
      set_Loading(false);
    }
  };


  const handleExport = () => {    
    if (gridRef.current) {     

      gridRef.current.api.exportDataAsExcel({
        fileName:
          "AssetHistoryReports_" + moment().format("YYYYMMDD") + ".xlsx",
        sheetName: "AssetHistory Report",
        processCellCallback: function (params) {
          return params.value;
        },
      });
    }
  };

  const handleMaximizeClick =()=>{    
    setIsMaximized(!isMaximized);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth={true}
      fullScreen={true}
      maxWidth="xl"
      PaperProps={{
        style: {

          height: isMaximized ? "100vh" : "90vh",
          width: isMaximized ? "100vw" : "80vw",
          margin: isMaximized ? 0 : "auto",      
        },
      }}
    >
      <DialogTitle
        style={{
          backgroundColor: "var(--primary-color)", // Set the background color of the dialog title
        }}
      >

        <div
          className="col-sm-12"
          style={{
            display: "flex",
            justifyContent: "left",
            alignItems: "center",
          }}
        >
          <div
            className="col-sm-11"
            style={{
              display: "flex",
              alignItems: "left",
              gap: "10px",
              color: "var(--aggrid-text-color)",
              fontSize: "1.5em",
            }}
          >
            Asset History
          </div>
          <div
            className="col-sm-1"
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "right",
            }}
          >
            {" "}
            <Tooltip title={isMaximized ? "Restore" : "Maximize"}>
              <IconButton
                onClick={handleMaximizeClick}
                style={{
                  cursor: "pointer",
                  alignItems: "flex-end",
                  color: "var(--aggrid-text-color)",
                  width: "20px",
                }}
              >
                {isMaximized ? <RestoreIcon /> : <MaximizeIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton
                onClick={onClose}
                style={{
                  cursor: "pointer",
                  alignItems: "flex-end",
                  color: "var(--aggrid-text-color)",
                  width: "20px",
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <div
          className="col-sm-12"
          style={{
            display: "flex",
            justifyContent: "right",
            alignItems: "center",
            margin: "1em",
          }}
        >
          <Tooltip title="Click to export the grid content in excel">
            <button
              type="button"
              onClick={handleExport}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
              aria-label="Export to Excel"
            >
              <img
                src={ExcelIcon}
                alt="Doc-Distro"
                className="assets-history-excel-icon"
              />
            </button>
          </Tooltip>
        </div>
        <div
          style={{ height: "90%", width: "100%" }}
          className="ag-theme-quartz-dark"
        >
          <AgGridReact
            columnDefs={columnDefs}
            rowData={rowData}
            ref={gridRef}
            headerHeight={30}
            rowGroupPanelShow="always"
          ></AgGridReact>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssetHistory;
